import { useMemo } from 'react'
import useCompanyNames from '../../hooks/useCompanyNames'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'

/**
 * @param {object} props
 * @param {object | null} props.positioning - msiBenchmark-shaped positioning block
 * @param {number | null} props.benchmarkGrowth - The benchmark mean, in `unit`
 * @param {string} [props.unit] - '%' (MSI effects, default) or a word like 'treden' (participatieladder)
 * @param {string} [props.respondentLabel] - Column label for the respondent count (default 'Respondenten')
 */
const BenchmarkAdminTable = ({ positioning, benchmarkGrowth, unit = '%', respondentLabel = 'Respondenten' }) => {
    const columns = [
        { key: 'rank', label: 'Positie' },
        { key: 'name', label: 'Organisatie' },
        { key: 'growth', label: 'Groei' },
        { key: 'respondentCount', label: respondentLabel },
    ]

    const distribution = positioning?.distribution || []
    const currentCompanyId = positioning?.currentCompany?.companyId

    // Gedeeld met de scatterplot op het themadetail van Intelligence.
    const nameMap = useCompanyNames(distribution.map(row => row.companyId))

    const rows = useMemo(() => {
        const sorted = distribution
            .map(row => ({
                ...row,
                name: nameMap.get(row.companyId) || row.companyId,
                isCurrent: row.companyId === currentCompanyId,
            }))
            .sort((a, b) => a.rank - b.rank)

        if (benchmarkGrowth != null && sorted.length > 0) {
            const splitIdx = sorted.findIndex(row => row.growth < benchmarkGrowth)
            if (splitIdx > 0 && splitIdx < sorted.length) {
                sorted.splice(splitIdx, 0, { _divider: true, companyId: '__divider__' })
            }
        }

        return sorted
    }, [distribution, nameMap, currentCompanyId, benchmarkGrowth])

    const renderRow = (row) => {
        if (row._divider) {
            return (
                <TableCell colSpan={4} className='bd-admin-divider-cell'>
                    <div className='bd-admin-divider' />
                </TableCell>
            )
        }

        const growthLabel = row.growth != null
            ? `${row.growth > 0 ? '+' : ''}${row.growth}${unit === '%' ? '%' : ` ${unit}`}`
            : '—'

        return (
            <>
                <TableCell>#{row.rank}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell className={row.growth >= 0 ? 'bd-stat-value--positive' : 'bd-stat-value--negative'}>
                    {growthLabel}
                </TableCell>
                <TableCell>{row.respondentCount ?? '—'}</TableCell>
            </>
        )
    }

    return (
        <Table
            columns={columns}
            items={rows}
            renderRow={renderRow}
            getItemKey={(row) => row.companyId}
            searchAccessor={(row) => row.name}
            searchPlaceholder='Zoeken op organisatie'
            emptyMessage='Geen organisaties gevonden.'
            showSearch={rows.length > 5}
            getRowClassName={(row) => row.isCurrent ? 'bd-admin-row--current' : ''}
        />
    )
}

export default BenchmarkAdminTable
